/**
 * Menu Scheduling API Service
 * 
 * Service layer for interacting with the menu scheduling backend APIs.
 */

import { api } from './api';

const menuSchedulingService = {
  /**
   * Get scheduled meals for a date range
   * @param {Date|string} startDate - Start date
   * @param {Date|string} endDate - End date
   * @param {string} token - Firebase ID token
   * @returns {Promise<Array>} Array of scheduled meals
   */
  async getScheduledMeals(startDate, endDate, token) {
    const start = startDate instanceof Date ? startDate.toISOString().split('T')[0] : startDate;
    const end = endDate instanceof Date ? endDate.toISOString().split('T')[0] : endDate;
    
    const { data } = await api.get('/menu-scheduling/meals', {
      params: { startDate: start, endDate: end },
      headers: { Authorization: `Bearer ${token}` }
    });
    return data;
  },

  /**
   * Schedule a recipe to a date
   * @param {number} recipeId - Recipe ID
   * @param {Date|string} date - Scheduled date
   * @param {string} mealType - Optional meal type ('breakfast', 'lunch', 'dinner', 'snack')
   * @param {string} notes - Optional notes
   * @param {boolean} requiresDayBeforePrep - Whether day-before prep is required
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Created scheduled meal
   */
  async scheduleMeal(recipeId, date, mealType = null, notes = null, requiresDayBeforePrep = null, token) {
    const scheduledDate = date instanceof Date ? date.toISOString().split('T')[0] : date;
    
    const { data } = await api.post('/menu-scheduling/meals', {
      recipe_id: recipeId,
      scheduled_date: scheduledDate,
      meal_type: mealType,
      notes: notes,
      requires_day_before_prep: requiresDayBeforePrep
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return data;
  },

  /**
   * Remove a scheduled meal
   * @param {string} mealId - Scheduled meal ID
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Success result
   */
  async removeScheduledMeal(mealId, token) {
    const { data } = await api.delete(`/menu-scheduling/meals/${mealId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return data;
  },

  /**
   * Update a scheduled meal
   * @param {string} mealId - Scheduled meal ID
   * @param {Object} updates - Fields to update
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Updated scheduled meal
   */
  async updateScheduledMeal(mealId, updates, token) {
    const { data } = await api.put(`/menu-scheduling/meals/${mealId}`, updates, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return data;
  },

  /**
   * Search recipes by name, ingredient, or vibe
   * @param {string} query - Search query
   * @param {string} searchType - 'name', 'ingredient', or 'vibe'
   * @param {string} vibe - Vibe tag (if searchType is 'vibe')
   * @param {string} token - Firebase ID token
   * @returns {Promise<Array>} Array of matching recipes
   */
  async searchRecipes(query, searchType = 'name', vibe = null, token) {
    const params = { searchType };
    if (searchType === 'vibe') {
      params.vibe = vibe;
    } else {
      params.query = query;
    }
    
    const { data } = await api.get('/menu-scheduling/search', {
      params,
      headers: { Authorization: `Bearer ${token}` }
    });
    return data;
  },

  /**
   * Get all available vibe tags
   * @param {string} token - Firebase ID token
   * @returns {Promise<Array>} Array of vibe tags
   */
  async getVibeTags(token) {
    const { data } = await api.get('/menu-scheduling/vibes', {
      headers: { Authorization: `Bearer ${token}` }
    });
    return data;
  },

  /**
   * Get vibe tags for a specific recipe
   * @param {number} recipeId - Recipe ID
   * @param {string} token - Firebase ID token
   * @returns {Promise<Array>} Array of vibe tags
   */
  async getRecipeVibeTags(recipeId, token) {
    const { data } = await api.get(`/menu-scheduling/vibes/${recipeId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return data;
  },

  /**
   * Add vibe tag to recipe
   * @param {number} recipeId - Recipe ID
   * @param {string} vibe - Vibe tag
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Created vibe tag
   */
  async addVibeTag(recipeId, vibe, token) {
    const { data } = await api.post('/menu-scheduling/vibes', {
      recipe_id: recipeId,
      vibe: vibe
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return data;
  }
};

export default menuSchedulingService;

